/**
 * Las ocho políticas de color. buildPalette no solo construye la paleta:
 * la corrige cuando incumple una regla y deja constancia de cada ajuste.
 */
import { clamp, mk, rng32 } from './color';
import type { AuditEntry, AuditState, HarmonyKey, PaletteConfig, Palette } from './types';

const HARMONIES: Record<HarmonyKey, { angles: number[]; label: string }> = {
  mono:          { angles: [0, 0, 0],       label: 'Monocromática' },
  analogous:     { angles: [0, 30, -28],    label: 'Análoga' },
  complementary: { angles: [0, 180, 158],   label: 'Complementaria' },
  split:         { angles: [0, 152, 208],   label: 'Complementaria dividida' },
  triadic:       { angles: [0, 120, 240],   label: 'Triádica' },
  tetradic:      { angles: [0, 90, 180],    label: 'Tetrádica' }
};
const isWarm = (h: number) => (h < 105 || h > 300);

export function buildPalette(cfg: PaletteConfig): Palette {
  const rnd = rng32(cfg.seed);
  const log: AuditEntry[] = [];
  const push = (state: AuditState, title: string, detail: string) => log.push({ state, title, detail });

  const H = HARMONIES[cfg.harmony];
  const jitter = cfg.harmony === 'mono' ? 0 : (rnd() * 10 - 5);

  /* P-01 · Rango de luminosidad utilizable para fondo.
     Ni negro ni blanco puros: no admiten sombra ni brillo y aplanan la imagen. */
  let L = cfg.light, fixedL = false;
  if (L < 0.12) { L = 0.12; fixedL = true; }
  if (L > 0.92) { L = 0.92; fixedL = true; }
  push(fixedL ? 'fix' : 'ok', 'Rango de luminosidad del soporte',
    fixedL ? `Luminosidad recortada a ${L.toFixed(2)}: fuera de 0.12–0.92 el fondo pierde recorrido para sombras y brillos.`
           : 'La base queda dentro de 0.12–0.92, con margen para sombras y realces.');

  /* Roles según el reparto 60/30/10 */
  const dom = mk(L, cfg.chroma, cfg.hue + H.angles[0]!, 'dominante');
  let sec = cfg.harmony === 'mono'
    ? mk(clamp(L + 0.14, 0, 1), cfg.chroma * 0.85, cfg.hue, 'secundario')
    : mk(clamp(L + (rnd() * 0.16 - 0.04), 0.08, 0.94), cfg.chroma * 0.9, cfg.hue + H.angles[1]! + jitter, 'secundario');
  let acc = cfg.harmony === 'mono'
    ? mk(clamp(L + 0.34, 0, 1), cfg.chroma * 1.15, cfg.hue, 'acento')
    : mk(clamp(L + (rnd() * 0.46 - 0.14), 0.1, 0.95), cfg.chroma * 1.25, cfg.hue + H.angles[2]! + jitter, 'acento');

  /* P-02 · Vibración cromática (contraste simultáneo).
     Dos matices casi opuestos, muy saturados y de luminosidad parecida hacen
     "vibrar" el borde. Se separa la luminosidad y se baja el croma del acento. */
  const dh = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};
  let vibra = false;
  [sec, acc].forEach((c, i) => {
    if (dh(c.h, dom.h) > 140 && c.C > 0.11 && dom.C > 0.11 && Math.abs(c.L - dom.L) < 0.14) {
      vibra = true;
      const dir = c.L >= dom.L ? 1 : -1;
      const fixed = mk(clamp(dom.L + dir * 0.24, 0.08, 0.95), c.C * 0.62, c.h, c.role);
      if (i === 0) sec = fixed; else acc = fixed;
    }
  });
  push(vibra ? 'fix' : 'ok', 'Vibración de complementarios',
    vibra ? 'Par casi opuesto con luminosidad e intensidad similares: se separó la luminosidad 0.24 y se bajó el croma un 38 %.'
          : 'Ningún par opuesto comparte luminosidad e intensidad altas.');

  /* P-03 · Temperatura dominante.
     Un fondo con calidez repartida al 50 % no tiene lectura; se deja la
     temperatura minoritaria únicamente en el acento (el 10 %). */
  let temp = false;
  if (isWarm(sec.h) !== isWarm(dom.h) && isWarm(acc.h) !== isWarm(dom.h)) {
    temp = true;
    sec = mk(sec.L, sec.C, dom.h + (sec.h > dom.h ? 34 : -34), 'secundario');
  }
  push(temp ? 'fix' : 'ok', 'Temperatura dominante',
    temp ? 'Dominante y secundario caían en temperaturas opuestas: el secundario se acercó a 34° de la base y el contraste térmico queda solo en el acento.'
         : `Familia ${isWarm(dom.h) ? 'cálida' : 'fría'} con el contraste térmico reservado al acento.`);

  /* P-04 · Ajuste a gamut sRGB por croma, no por recorte de canal. */
  const clipped = [dom, sec, acc].filter(c => c.clipped).length;
  push(clipped ? 'fix' : 'ok', 'Gamut sRGB',
    clipped ? `${clipped} color(es) excedían sRGB: se redujo el croma manteniendo matiz y luminosidad, en vez de recortar por canal.`
            : 'Los tres roles caen dentro de sRGB sin ajuste de croma.');

  /* P-05 · Neutros teñidos: el gris puro junto a un fondo con color se ve sucio. */
  const light = mk(0.965, Math.min(0.022, dom.C * 0.2), dom.h, 'claro');
  const dark = mk(0.185, Math.min(0.03, dom.C * 0.25), dom.h, 'oscuro');
  push('ok', 'Neutros teñidos',
    `Claro y oscuro llevan un resto de croma (≤0.03) sobre el matiz ${Math.round(dom.h)}°; el gris neutro se vería sucio al lado del fondo.`);

  /* P-06 · Mezcla en espacio perceptual. */
  const worst = Math.max(dh(dom.h, sec.h), dh(sec.h, acc.h));
  push('ok', 'Interpolación perceptual',
    `Salto máximo de ${Math.round(worst)}° recorrido por el arco corto en OKLCH; en sRGB esa transición pasaría por gris.`);

  return { dom, sec, acc, light, dark, log, harmony: H.label };
}
